/**
 * Entity Type to App Type Mapping
 * 
 * Centralized logic for determining which app should open for a given entity type.
 * This is the single source of truth for entity -> app mappings.
 */

import { entityTypeRegistry } from '../services/entityTypeRegistry';

/**
 * Determine the appropriate app type for an entity
 * Handles both legacy types (PDF, Doc, Note) and hierarchical types (File.Document.PDF, etc.)
 */
export function getAppTypeForEntity(entity: any): string {
  const entityType = entity.entityType || entity.type;
  
  if (entityTypeRegistry.isInitialized()) {
    try {
      const appType = entityTypeRegistry.getRegisteredAppType(entityType);
      if (appType !== null) {
        return appType;
      }
    } catch (error) {
      // Registry lookup failed — fall through to legacy resolution
    }
  }
  
  return getLegacyAppTypeForEntity(entity);
}

/**
 * Legacy app type resolution (fallback)
 * Used until all types are migrated to EntityTypeDefinition
 */
function getLegacyAppTypeForEntity(entity: any): string {
  const entityType = entity.entityType || entity.type;
  const skeleton = entity.skeleton as any;
  
  // AppInstance entities — use the appId stored in skeleton (same value as window.appType)
  if (entityType === 'AppInstance') {
    const appId = skeleton?.appId;
    if (appId) {
      return appId;
    }
  }

  // AppProject and WebsiteProject → App Builder IDE
  if (entityType === 'app_project' || entityType === 'AppProject') {
    return 'app-builder';
  }

  if (entityType === 'website_project' || entityType === 'WebsiteProject') {
    return 'app-builder';
  }
  
  // Generated App entities — use the appId as the appType
  if (entityType === 'App') {
    const appId = skeleton?.appId;
    if (appId) {
      return appId;
    }
  }

  // Manuscript Review Report — dedicated report app
  if (entityType === 'ManuscriptReviewReport') {
    return 'manuscript-review-report';
  }

  // Dashboard — AI-generated structured report with charts and metrics
  // Accept both 'Dashboard' (canonical) and 'dashboard' (lowercase, AI-created)
  if (entityType === 'Dashboard' || entityType === 'dashboard') {
    return 'dashboard';
  }

  // Entity types that use the generic entity viewer
  const specialViewTypes = [
    'StoryCharacter',
    'NSFWPromptTemplate',
    'ManuscriptReport',
    'ManuscriptDiscoveryReport',
    'CitationVerificationReport',
    'Rule',
    'Window',
    'NewWindow',
    'OpenDialog',
    'SearchResults',
    'ComputeSession',
    // CSV and spreadsheets use CSVTableView (EntityViewApp) for better data grid UX
    'File.Data.CSV',
    'File.Spreadsheet.Excel',
    'File.Spreadsheet.OpenDocument',
  ];
  
  if (specialViewTypes.includes(entityType)) {
    return 'entity-view';
  }
  
  // External integrations
  if (entityType === 'GoogleDriveFile' || entityType?.startsWith('External.GoogleDrive')) {
    return 'google-drive-file';
  }
  
  if (entityType === 'LocalMount') {
    return 'local-file';
  }

  if (entityType === 'Codebase') {
    return 'entity-view';
  }
  
  if (entityType === 'LocalFile' || entityType?.startsWith('External.LocalFile')) {
    return 'local-file';
  }
  
  // Legal/verified sources
  if (entityType === 'VerifiedSource' || entityType === 'LegalCode') {
    return 'legal-code-viewer';
  }
  
  // Doc entities - but check for special file types
  if (entityType === 'Doc') {
    const fileName = skeleton?.fileName || entity.name || '';
    
    if (skeleton?.mimeType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf')) {
      return 'pdfium';
    }
    
    // ⚠️ DISABLED: v2 documents not ready, use v1 for all
    // if (skeleton?.version === 'v2' || skeleton?.architecture === 'unified') {
    //   return 'docs-v2';
    // }
    
    return 'docs';
  }
  
  // Canvas entities — always use v2
  if (entityType === 'Canvas') {
    return 'canvas-v2';
  }
  
  // Sheet entities (v2 only)
  if (entityType === 'Sheet' || entityType === 'Spreadsheet') {
    return 'sheets-v2';
  }
  
  // Presentation entities (v2 only)
  if (entityType === 'Presentation' || entityType === 'Slide' || entityType === 'Slides') {
    return 'presentations-v2';
  }
  
  // Page entities (v2 block editor)
  if (entityType === 'Page') {
    return 'page-v2';
  }

  // ✅ InboxApp — opens the InboxApp view
  if (entityType === 'InboxApp') {
    return 'entity-view';
  }

  // Note entities — always use v2
  if (entityType === 'Note' || entityType === 'Content.Note') {
    return 'notes-v2';
  }

  // Prompt / Memory / MarkdownDoc entities
  if (entityType === 'Prompt') return 'prompts-v2';
  if (entityType === 'Memory') return 'memory-v2';
  if (entityType === 'MarkdownDoc') return 'markdown-v2';
  
  // PDF entities (both legacy and hierarchical)
  if (entityType === 'PDF' || 
      entityType === 'File.Document.PDF' || 
      entityType?.startsWith('File.Document.PDF')) {
    return 'pdfium';
  }
  
  // Hierarchical File types
  if (entityType?.startsWith('File.Image')) {
    return 'mediaviewer';
  }
  
  if (entityType?.startsWith('File.Data')) {
    return 'file-editor';
  }
  
  if (entityType?.startsWith('File.Code')) {
    return 'file-editor';
  }
  
  if (entityType?.startsWith('File.Audio')) {
    return 'file-viewer'; // Shows unsupported message inside FileEditorApp
  }
  
  if (entityType?.startsWith('File.Video')) {
    return 'mediaviewer';
  }
  
  if (entityType?.startsWith('File.Archive')) {
    return 'file-viewer';
  }
  
  if (entityType?.startsWith('File.Spreadsheet')) {
    return 'sheets-v2';
  }
  
  if (entityType?.startsWith('File.Presentation')) {
    return 'presentations-v2';
  }
  
  if (entityType?.startsWith('File.Document')) {
    console.log('[entityTypeMapping] ✅ File.Document matched, routing to file-editor:', {
      entityType,
      fileName: skeleton?.fileName || entity.name
    });
    return 'file-editor';
  }
  
  // Generic File type - check by filename extension as last resort
  if (entityType === 'File' || entityType?.startsWith('File.')) {
    const fileName = skeleton?.fileName || entity.name || '';
    
    if (skeleton?.mimeType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf')) {
      return 'pdfium';
    }
    
    if (fileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/)) {
      return 'mediaviewer';
    }
    
    if (fileName.toLowerCase().match(/\.(py|js|ts|tsx|jsx|java|cpp|c|h|cs|go|rs|php|rb|swift|html|css|scss)$/)) {
      console.log('[entityTypeMapping] Code file detected by extension, routing to file-editor');
      return 'file-editor';
    }
    
    if (fileName.toLowerCase().match(/\.(json|xml|yaml|yml|csv)$/)) {
      console.log('[entityTypeMapping] Data file detected by extension, routing to file-editor');
      return 'file-editor';
    }
    
    if (fileName.toLowerCase().match(/\.(txt|md)$/)) {
      return 'file-editor';
    }
    
    return 'webdocument';
  }
  
  // SpaceTemplate entities — open StoreApp and navigate to the template detail
  if (entityType === 'SpaceTemplate') {
    return 'store';
  }

  // Contact entities — ContactsApp navigated to that contact
  if (entityType === 'Contact') {
    return 'contacts';
  }

  // TasksApp entity types
  if (entityType === 'Project' || entityType === 'Area') {
    return 'tasks';
  }

  // Goal entities — GoalsApp navigated to that goal. See APP_ARCHITECTURE_GUIDE_V3.md §10
  if (entityType === 'Goal') {
    return 'goals';
  }

  // Task entities — TasksApp navigated to and expanded on that task. See APP_ARCHITECTURE_GUIDE_V3.md §10
  if (entityType === 'Task') {
    return 'tasks';
  }
  
  // API Connection and MCP Connection entities → unified IntegrationsApp
  if (entityType === 'APIConnection') {
    return 'integrations';
  }

  if (entityType === 'MCPConnection') {
    return 'integrations';
  }
  
  // Integration entities (Gmail, Calendar, Analytics, Drive, Ads, Plaid, etc.)
  if (entityType === 'Integration' || entityType?.startsWith('Integration.')) {
    return 'entity-view';
  }
  
  // Automation entities (AutomationsApp) — also matches legacy 'Agent' entities
  if (entityType === 'Automation' || entityType === 'Agent') {
    return 'automations';
  }

  if (entityType === 'WebDocument') {
    return 'browser';
  }
  
  if (entityType === 'BrowserWindow' || entityType === 'BrowserPage') {
    return 'browser';
  }
  
  if (entityType === 'Chat') {
    return 'chat';
  }
  
  if (entityType === 'Space') {
    return 'entityBrowser';
  }
  
  if (entityType === 'Group') {
    return 'group';
  }
  
  // Integration.Plaid and BankAccount open inside BankApp
  if (entityType === 'Integration.Plaid' || entityType === 'BankAccount') {
    return 'bank';
  }

  // Mail.Thread, Mail.Draft, and Mail.Message open inside the full MailApp
  // (routable pattern): the app navigates to the thread / composer.
  // Mail.Message routing navigates to the parent thread via skeleton.threadId.
  if (entityType === 'Mail.Thread' || entityType === 'Mail.Draft' || entityType === 'Mail.Message') {
    return 'mail-app';
  }

  if (entityType === 'Messaging.Conversation') {
    return 'messaging-app';
  }

  // Other mail entity types still use the generic entity viewer
  if (entityType === 'Mail.Contact' ||
      entityType === 'Mail.Audience' ||
      entityType === 'Mail.Broadcast') {
    return 'entity-view';
  }
  
  // Writers Dashboard
  if (entityType === 'WritersDashboard') {
    return 'entity-view';
  }

  // Book formatter
  if (entityType === 'BookProject') {
    return 'book_formatter_v2';
  }

  // Entity Graph extracted types
  if (
    entityType === 'Property' || entityType === 'Company' ||
    entityType === 'Deal' || entityType === 'LegalEntity' ||
    entityType === 'LegalFiling' || entityType === 'SecurityInstrument' ||
    entityType === 'Transaction' || entityType === 'Insurance' ||
    entityType === 'Subscription' || entityType === 'ExtractionRun'
  ) {
    return 'entity-graph';
  }

  // Social media imports
  if (entityType === 'Social.Tweet') {
    return 'entity-view';
  }

  // Twitter archive integration record
  if (entityType === 'Integration.TwitterArchive') {
    return 'entity-view';
  }

  // Browser extension entities
  if (entityType === 'Browser.Visit') {
    return 'browser';
  }
  if (entityType === 'Content.WebDocument' || entityType === 'WebDocument') {
    return 'browser';
  }

  // Default fallback
  return 'entity-view';
}
